import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Field, Icon, TextInput } from "@zapier/design-system";
import { Button } from "@zapier/design-system-beta";
import {
  zslExecutionFailedErrorMsgAtom,
  zslAtom,
  emitAiPredictionUserFeedbackEventAtom,
  zslFeedbackPredictionIdAtom,
  togglesAtomResettable,
} from "@zapier/canvas-state";
import { IMPLICIT_BAD } from "@zapier/canvas-constants";

import { useZslPieceCompletion } from "../../hooks/useZslPieceCompletion";
import { aiPromptAtom } from "../../atoms/aiPromptAtom";
import { tuningPromptsAtom } from "../../atoms/tuningPromptsAtom";
import { renderZslToCanvasCommand } from "../../atoms/renderZslToCanvasCommand";
import { isStreamingZslAtom } from "../../atoms/streamingStatusAtoms";
import { AiSparkle } from "../AiSparkle";
import { ChatInterface } from "../ChatInterface/ChatInterface";
import styles from "./ModifyPromptSidebar.module.css";
import { streamChatResponse } from "../../../../service-providers/src/zsl-creator/chatService";

export function ModifyPromptSidebar() {
  const prompt = useAtomValue(aiPromptAtom);
  const [additionalChanges, setAdditionalChanges] = useState("");
  const submissions = useAtomValue(tuningPromptsAtom);
  const [error, setError] = useState("");
  const submitZslToCanvas = useSetAtom(renderZslToCanvasCommand);
  const isLoading = useAtomValue(isStreamingZslAtom);
  const toggles = useAtomValue(togglesAtomResettable);

  const { handleSubmit } = useZslPieceCompletion({
    api: "/api/zsl/v2/combined_stream",
    async onComplete() {
      await submitZslToCanvas();
    },
    onError(error: string) {
      setError(error);
    },
  });

  const handleTextAreaChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setAdditionalChanges(e.target.value);
    },
    [setAdditionalChanges],
  );

  const handleSendMessage = useAtomCallback(
    useCallback(
      async (get, set, e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);
        const message = formData.get("message") as string;

        if (!message || !message.trim()) return;

        // Clear the input field after submission
        e.currentTarget.reset();

        const zsl = get(zslAtom);
        const prediction_id = get(zslFeedbackPredictionIdAtom);

        // Create an abort controller for the stream
        const abortController = new AbortController();

        try {
          // Add the user's prompt to the list
          set(tuningPromptsAtom, (prev) => [...prev, message]);

          // Clear any previous error messages
          set(zslExecutionFailedErrorMsgAtom, null);

          // Send implicit feedback for the previous response
          set(emitAiPredictionUserFeedbackEventAtom, {
            prediction_id,
            correlation_id: null,
            user_rating: IMPLICIT_BAD,
            user_feedback: JSON.stringify({
              prevZsl: zsl,
              tuningPrompts: [...get(tuningPromptsAtom)],
            }),
          });

          // Use the streaming API to process the message
          await streamChatResponse(
            {
              messages: [
                {
                  role: "user",
                  content: message,
                },
              ],
            },
            {
              onPartial: () => {
                // Handle partial response if needed
              },
              onComplete: async (data) => {
                // When complete, update the ZSL with the final response

                // If the response succeeded, submit the ZSL to canvas
                if (!data.error) {
                  await submitZslToCanvas();
                }
              },
              onError: (error) => {
                console.error("Chat response error:", error);
                setError(
                  error.error || "An error occurred processing your message",
                );
                set(
                  zslExecutionFailedErrorMsgAtom,
                  error.error || "Failed to process message",
                );
              },
            },
            abortController.signal,
          );
        } catch (error) {
          console.error("Error in handleSendMessage:", error);
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error occurred";
          setError(errorMessage);
          set(zslExecutionFailedErrorMsgAtom, errorMessage);
        }
      },
      [submitZslToCanvas],
    ),
  );

  const handleFormSubmit = useAtomCallback(
    useCallback(
      async (get, set, e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const trimmedChanges = additionalChanges.trim();
        if (trimmedChanges) {
          const prediction_id = get(zslFeedbackPredictionIdAtom);
          const prompt = get(aiPromptAtom);
          const zsl = get(zslAtom);
          const prevTuningPrompts = get(tuningPromptsAtom);
          const tuningPrompts = [...prevTuningPrompts, trimmedChanges];

          // Add new submission to the list
          set(tuningPromptsAtom, (prev) => [...prev, trimmedChanges]);

          // Clear the input field
          setAdditionalChanges("");

          set(emitAiPredictionUserFeedbackEventAtom, {
            prediction_id,
            correlation_id: null,
            user_rating: IMPLICIT_BAD,
            user_feedback: JSON.stringify({
              prevZsl: zsl,
              tuningPrompts,
            }),
          });

          set(zslExecutionFailedErrorMsgAtom, null); // Clear out old error alert
          await handleSubmit(prompt, zsl, tuningPrompts);

          /* TODO: Disable / hide x button while updating system */
        }
      },
      [additionalChanges, handleSubmit],
    ),
  );

  // Render based on feature flag
  if (toggles.chatComposer) {
    return (
      <div className={styles.container}>
        <ChatInterface onSubmit={handleSendMessage} isLoading={isLoading} />
      </div>
    );
  }

  // Original implementation for when the feature flag is off
  return (
    <form className={styles.form} onSubmit={handleFormSubmit}>
      <p className={styles.description}>
        Add to the original prompt and let Copilot fine-tune the generated
        system.
      </p>
      <Field
        label="Original prompt"
        renderInput={(inputProps) => (
          <TextInput
            {...inputProps}
            isMultiline={true}
            isDisabled={true}
            disabledText=""
            name="Original prompt"
            rows={3}
            placeholder={prompt}
          />
        )}
      />
      {submissions.length > 0 && (
        <div>
          {submissions.map((submission, index) => (
            <div key={index} className={styles.submission}>
              <div className={styles.leftColumn}>
                <AiSparkle height={20} width={20} />
              </div>
              <p>{submission}</p>
            </div>
          ))}
        </div>
      )}
      <Field
        isRequired={true}
        label="Additional changes"
        error={
          error ? `We couldn't process your changes. Try again.` : undefined
        }
        renderInput={(inputProps) => (
          <TextInput
            {...inputProps}
            isMultiline={true}
            rows={3}
            value={additionalChanges}
            onChange={handleTextAreaChange}
            name="message"
          />
        )}
      />
      <Button
        type="submit"
        isDisabled={!additionalChanges}
        isLoading={isLoading}
      >
        <Icon name="miscAI" size={30} />
        Update system
      </Button>
    </form>
  );
}
